import { useCallback, useEffect, useState } from 'react'

import { SelectionColor } from '~/bootcamp/Activities/Algebra/Components/SquareColorPicker'

import { generateEquationForPowerColor, ClickablePartsModel, checkAnswer } from './PowerColor.utils'

interface UsePowerColorReturnType {
  clickableParts?: ClickablePartsModel[]
  formValid: boolean
  handleCheckAnswer: () => void
  onInputChange: (val: number, color: SelectionColor) => void
  goToNextQuestion: () => void
  clearForm: () => void
}

interface UsePowerColorProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function UsePowerColor({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
}: UsePowerColorProps): UsePowerColorReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [clickableParts, setClickableParts] = useState<ClickablePartsModel[]>([])
  const [equationString, setEquationString] = useState('')

  useEffect(() => {
    const [_clickableParts, _equationString] = generateEquationForPowerColor(difficultyLevel, taskHistory)
    setClickableParts(_clickableParts)
    setEquationString(_equationString)
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    const isRight = checkAnswer(clickableParts)
    onAnswerStateChange(isRight)
    if (!isRight) {
      setTimeout(() => {
        onAnswerStateChange(null)
      }, 1500)
    }
  }, [onAnswerStateChange, clickableParts])

  const onInputChange = useCallback(
    (index: number, activeColor: SelectionColor) => {
      setClickableParts((prevState) => {
        const _clickableParts: ClickablePartsModel[] = JSON.parse(JSON.stringify(prevState))
        _clickableParts[index].color = _clickableParts[index].color === activeColor ? undefined : activeColor
        return _clickableParts
      })
      onAnswerStateChange(null)
    },
    [onAnswerStateChange],
  )

  const clearForm = useCallback(() => {
    setClickableParts((prevState) => {
      const _clickableParts: ClickablePartsModel[] = JSON.parse(JSON.stringify(prevState))
      _clickableParts.forEach((item) => {
        item.color = undefined
      })
      return _clickableParts
    })
  }, [])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [equationString, ...prevState].slice(0, 3))
    onNextQuestionClick()
  }, [onNextQuestionClick, equationString])

  return {
    clickableParts,
    formValid: true,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    clearForm,
  }
}

export default UsePowerColor
